import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["link", "section", "progressText", "progressFill", "submitButton"]

  connect() {
    this.handleScroll = this.handleScroll.bind(this)
    this.handleHashChange = this.handleHashChange.bind(this)
    this.scheduleProgressUpdate = this.scheduleProgressUpdate.bind(this)
    this.ticking = false
    this.progressUpdateTimeout = null

    window.addEventListener("scroll", this.handleScroll, { passive: true })
    window.addEventListener("hashchange", this.handleHashChange)
    this.element.addEventListener("input", this.scheduleProgressUpdate)
    this.element.addEventListener("change", this.scheduleProgressUpdate)
    this.element.addEventListener("click", this.scheduleProgressUpdate)

    this.activateFromHash() || this.updateActiveSection()
    this.updateProgress()
  }

  disconnect() {
    window.removeEventListener("scroll", this.handleScroll)
    window.removeEventListener("hashchange", this.handleHashChange)
    this.element.removeEventListener("input", this.scheduleProgressUpdate)
    this.element.removeEventListener("change", this.scheduleProgressUpdate)
    this.element.removeEventListener("click", this.scheduleProgressUpdate)
    window.clearTimeout(this.progressUpdateTimeout)
  }

  activate(event) {
    event.preventDefault()

    const id = event.currentTarget.hash?.slice(1)
    if (!id) return

    this.scrollToSection(id)
    this.activateSection(id)
    history.pushState(null, "", `#${id}`)
  }

  handleScroll() {
    if (this.ticking) return

    this.ticking = true
    window.requestAnimationFrame(() => {
      this.updateActiveSection()
      this.ticking = false
    })
  }

  handleHashChange() {
    this.activateFromHash()
  }

  scheduleProgressUpdate() {
    window.clearTimeout(this.progressUpdateTimeout)
    this.progressUpdateTimeout = window.setTimeout(() => this.updateProgress(), 60)
  }

  activateFromHash() {
    const id = window.location.hash.slice(1)
    if (!id) return false

    return this.activateSection(id)
  }

  updateActiveSection() {
    if (this.scrolledToPageBottom()) {
      this.activateSection(this.sectionTargets[this.sectionTargets.length - 1]?.id)
      return
    }

    const line = this.activationLine()
    let activeSection = this.sectionTargets[0]

    this.sectionTargets.forEach((section) => {
      if (section.getBoundingClientRect().top <= line) {
        activeSection = section
      }
    })

    if (activeSection) this.activateSection(activeSection.id)
  }

  activationLine() {
    const navbar = document.querySelector(".navbar-shell")
    const navbarBottom = navbar?.getBoundingClientRect().bottom || 0

    return navbarBottom + 48
  }

  activateSection(id) {
    let activated = false

    this.linkTargets.forEach((link) => {
      const active = link.hash === `#${id}`
      link.closest(".course-form-steps__item")?.classList.toggle("course-form-steps__item--active", active)
      activated ||= active
    })

    return activated
  }

  scrollToSection(id) {
    const section = this.sectionTargets.find((candidate) => candidate.id === id)
    if (!section) return

    const navbar = document.querySelector(".navbar-shell")
    const navbarHeight = navbar?.getBoundingClientRect().height || 0
    const top = section.getBoundingClientRect().top + window.scrollY - navbarHeight - 24

    window.scrollTo({ top, behavior: "smooth" })
    window.setTimeout(() => this.updateActiveSection(), 300)
  }

  scrolledToPageBottom() {
    return window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4
  }

  updateProgress() {
    let allRequiredFieldsComplete = true

    this.sectionTargets.forEach((section) => {
      const requiredFields = Array.from(section.querySelectorAll("[data-form-section-nav-required]"))
        .filter((field) => this.isRelevantField(field))
      const total = requiredFields.length
      const complete = requiredFields.filter((field) => {
        const fieldIsComplete = this.fieldComplete(field)
        this.syncLiveValidity(field, fieldIsComplete)
        return fieldIsComplete
      }).length
      const link = this.linkTargets.find((candidate) => candidate.hash === `#${section.id}`)
      const item = link?.closest(".course-form-steps__item")
      const index = this.linkTargets.indexOf(link)
      const progressText = this.progressTextTargets[index]
      const progressFill = this.progressFillTargets[index]
      const ratio = total > 0 ? complete / total : 0

      if (progressText) progressText.textContent = `${complete}/${total}`
      if (progressFill) progressFill.style.width = `${Math.round(ratio * 100)}%`

      item?.classList.toggle("course-form-steps__item--complete", total > 0 && complete === total)
      item?.classList.toggle("course-form-steps__item--partial", total > 0 && complete > 0 && complete < total)
      item?.classList.toggle("course-form-steps__item--empty", total > 0 && complete === 0)

      if (complete < total) allRequiredFieldsComplete = false
    })

    this.syncSubmitButton(allRequiredFieldsComplete)
  }

  syncSubmitButton(enabled) {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = !enabled
    this.submitButtonTarget.toggleAttribute("aria-disabled", !enabled)

    if (enabled) {
      this.submitButtonTarget.removeAttribute("title")
    } else {
      this.submitButtonTarget.title = "Nejdříve doplňte a opravte všechna povinná pole."
    }
  }

  isRelevantField(field) {
    return !this.hidden(field) && !this.disabled(field)
  }

  fieldComplete(field) {
    switch (field.dataset.formSectionNavFieldType) {
      case "checkbox":
        return this.checkboxComplete(field)
      case "multi_select":
        return this.multiSelectComplete(field)
      case "date_range":
        return this.dateRangeComplete(field)
      case "marketing_variants":
        return this.marketingVariantsComplete(field)
      case "file":
        return this.fileComplete(field)
      default:
        return this.defaultFieldComplete(field)
    }
  }

  defaultFieldComplete(field) {
    const control = this.controls(field).find((element) => element.type !== "hidden")
    if (!control) return false

    return this.controlComplete(control)
  }

  checkboxComplete(field) {
    const checkbox = field.querySelector("input[type='checkbox']")
    return !!checkbox?.checked
  }

  multiSelectComplete(field) {
    if (field.querySelector(".form-field__select-panel-chip")) return true
    if (field.querySelector("[aria-checked='true'], [aria-selected='true']")) return true

    return Array.from(field.querySelectorAll("input[type='hidden']")).some((input) => this.valuePresent(input.value))
  }

  dateRangeComplete(field) {
    if (field.querySelector(".form-field__date-range-row--invalid")) return false
    if (this.dateRangeRows(field).some((row) => this.dateRangeRowInvalid(row))) return false

    return this.dateRangeRows(field).some((row) => this.dateRangeRowComplete(row))
  }

  marketingVariantsComplete(field) {
    const rows = Array.from(field.querySelectorAll(".form-field__marketing-variant-row"))
      .filter((row) => !this.hidden(row))
    if (rows.length === 0) return false

    const rowsComplete = rows.every((row) => {
      const targetGroup = row.querySelector("[data-marketing-variant-target-group]")
      const price = row.querySelector("[data-marketing-variant-price]")
      const capacity = row.querySelector("input[name$='[capacity]']")
      return this.controlComplete(targetGroup) && this.controlComplete(price) && this.controlComplete(capacity)
    })

    return rowsComplete && this.marketingVariantCapacitySumValid(rows)
  }

  fileComplete(field) {
    const input = field.querySelector("input[type='file']")
    return !!input?.files?.length || !!field.querySelector(".file-upload-preview")
  }

  controls(field) {
    return Array.from(field.querySelectorAll("input, select, textarea, button"))
      .filter((control) => control.type !== "hidden" && !this.hidden(control))
  }

  disabled(field) {
    const controls = this.controls(field)
    return controls.length > 0 && controls.every((control) => control.disabled)
  }

  hidden(element) {
    return !!element.closest("[hidden]") || element.offsetParent === null
  }

  syncLiveValidity(field, complete) {
    const invalid = (this.fieldHasValue(field) || this.fieldDependsOnExternalValue(field)) && !complete

    field.classList.toggle("form-field--live-invalid", invalid)
    this.controls(field).forEach((control) => {
      control.toggleAttribute("aria-invalid", invalid && this.valuePresent(control.value) && !this.controlValid(control))
    })

    if (field.dataset.formSectionNavFieldType === "date_range") {
      this.dateRangeRows(field).forEach((row) => {
        row.classList.toggle("form-field__date-range-row--live-invalid", this.dateRangeRowInvalid(row))
      })
    } else if (field.dataset.formSectionNavFieldType === "marketing_variants") {
      this.syncMarketingVariantLiveValidity(field)
    }
  }

  fieldHasValue(field) {
    if (field.dataset.formSectionNavFieldType === "file") return this.fileComplete(field)
    if (field.dataset.formSectionNavFieldType === "checkbox") return !!field.querySelector("input[type='checkbox']")?.checked

    return Array.from(field.querySelectorAll("input, select, textarea"))
      .some((control) => control.type !== "hidden" && !this.hidden(control) && this.valuePresent(control.value))
  }

  fieldDependsOnExternalValue(field) {
    return field.dataset.formSectionNavFieldType === "marketing_variants" && (this.expectedStudentCount() || 0) > 0
  }

  controlComplete(control) {
    return !!control && this.valuePresent(control.value) && this.controlValid(control)
  }

  controlValid(control) {
    if (typeof control.checkValidity === "function" && !control.checkValidity()) return false
    if (!this.dateMinimumValid(control)) return false

    return true
  }

  dateMinimumValid(control) {
    const minimum = control.dataset.formSectionNavMinDate
    if (!minimum) return true

    const value = this.dateValue(control.value)
    if (!value) return false

    return value >= this.minimumDate(minimum)
  }

  dateRangeOrderValid(startValue, endValue) {
    const start = this.dateValue(startValue)
    const end = this.dateValue(endValue)

    return !!start && !!end && end >= start
  }

  dateRangeRows(field) {
    return Array.from(field.querySelectorAll(".form-field__date-range-row"))
  }

  dateRangeRowComplete(row) {
    const start = row.querySelector("[data-date-range-start]")
    const end = row.querySelector("[data-date-range-end]")

    return this.controlComplete(start) && this.controlComplete(end) && this.dateRangeOrderValid(start.value, end.value)
  }

  dateRangeRowInvalid(row) {
    const start = row.querySelector("[data-date-range-start]")
    const end = row.querySelector("[data-date-range-end]")
    const startPresent = this.valuePresent(start?.value)
    const endPresent = this.valuePresent(end?.value)

    if (!startPresent && !endPresent) return false
    if (startPresent && !this.controlValid(start)) return true
    if (endPresent && !this.controlValid(end)) return true
    if (startPresent !== endPresent) return true

    return !this.dateRangeOrderValid(start.value, end.value)
  }

  marketingVariantCapacitySumValid(rows) {
    const expectedStudentCount = this.expectedStudentCount()
    if (expectedStudentCount === null || expectedStudentCount <= 0) return true

    return this.marketingVariantCapacitySum(rows) === expectedStudentCount
  }

  marketingVariantCapacitySum(rows) {
    return rows.reduce((sum, row) => {
      const capacity = row.querySelector("input[name$='[capacity]']")
      return sum + (this.numberValue(capacity?.value) || 0)
    }, 0)
  }

  syncMarketingVariantLiveValidity(field) {
    const rows = Array.from(field.querySelectorAll(".form-field__marketing-variant-row"))
      .filter((row) => !this.hidden(row))
    const capacitySumInvalid = !this.marketingVariantCapacitySumValid(rows)

    rows.forEach((row) => {
      const targetGroup = row.querySelector("[data-marketing-variant-target-group]")
      const price = row.querySelector("[data-marketing-variant-price]")
      const capacity = row.querySelector("input[name$='[capacity]']")

      targetGroup?.toggleAttribute("aria-invalid", this.valuePresent(targetGroup.value) && !this.controlValid(targetGroup))
      price?.toggleAttribute("aria-invalid", this.valuePresent(price.value) && !this.controlValid(price))
      capacity?.toggleAttribute("aria-invalid", (this.valuePresent(capacity.value) && !this.controlValid(capacity)) || capacitySumInvalid)
    })
  }

  expectedStudentCount() {
    const input = this.element.querySelector("[name='course[expected_student_count]']")
    return this.numberValue(input?.value)
  }

  numberValue(value) {
    const normalizedValue = value?.toString().trim()
    if (!normalizedValue) return null

    const number = Number(normalizedValue.replace(",", "."))
    return Number.isFinite(number) ? number : null
  }

  dateValue(value) {
    const normalizedValue = value?.toString().trim()
    if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue || "")) return null

    return normalizedValue
  }

  minimumDate(minimum) {
    const date = new Date()
    if (minimum === "tomorrow") date.setDate(date.getDate() + 1)

    const year = date.getFullYear()
    const month = `${date.getMonth() + 1}`.padStart(2, "0")
    const day = `${date.getDate()}`.padStart(2, "0")

    return `${year}-${month}-${day}`
  }

  valuePresent(value) {
    if (value === undefined || value === null) return false

    const normalizedValue = value.toString().trim()
    if (normalizedValue.length === 0) return false

    return true
  }
}
